import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface Option {
  id: number;
  value: string;
}

export interface SchemesFilterValues {
  governmentType: number | null;
  categoryId: number | null;
  fromDate: string;
  toDate: string;
}

interface FilterSchemesProps extends SchemesFilterValues {
  open: boolean;
  onClose: () => void;
  governmentList: Option[];
  categoryList: Option[];
  onApply: (filters: SchemesFilterValues) => void;
}

const popoverPaperSx = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
} as const;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FilterSchemes({
  open,
  onClose,
  governmentList,
  categoryList,
  governmentType,
  categoryId,
  fromDate,
  toDate,
  onApply,
}: FilterSchemesProps) {
  const [_governmentType, _setGovernmentType] = useState<number | null>(governmentType);
  const [_categoryId, _setCategoryId] = useState<number | null>(categoryId);
  const [_fromDate, _setFromDate] = useState<string>(fromDate);
  const [_toDate, _setToDate] = useState<string>(toDate);

  const handleClose = () => onClose();

  const handleReset = () => {
    onApply({ governmentType: null, categoryId: null, fromDate: "", toDate: "" });
    onClose();
  };

  const handleApply = () => {
    onApply({
      governmentType: _governmentType,
      categoryId: _categoryId,
      fromDate: _fromDate,
      toDate: _toDate,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Schemes
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Government Type
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={governmentList.find((option) => option.id === _governmentType) || null}
            onChange={(_, value) => _setGovernmentType(value?.id ?? null)}
            options={governmentList}
            getOptionLabel={(option) => option.value}
            componentsProps={{ paper: popoverPaperSx }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select government type" />
            )}
          />

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={0.5}>
            Category Type
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={categoryList.find((option) => option.id === _categoryId) || null}
            onChange={(_, value) => _setCategoryId(value?.id ?? null)}
            options={categoryList}
            getOptionLabel={(option) => option.value}
            componentsProps={{ paper: popoverPaperSx }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select category type" />
            )}
          />

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={1}>
            Uploaded Date
          </Typography>
          <Box display="flex" gap={1.5}>
            <DatePicker
              label="From Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_fromDate ? dayjs(_fromDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setFromDate(value?.format("YYYY-MM-DD") || "");
                if (_toDate && value && dayjs(_toDate, "YYYY-MM-DD").isBefore(value)) {
                  _setToDate("");
                }
              }}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              format="DD-MM-YYYY"
              disableFuture
              minDate={_fromDate ? dayjs(_fromDate, "YYYY-MM-DD") : undefined}
              value={_toDate ? dayjs(_toDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setToDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
